import { Component, ElementRef, Input, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import moment from 'moment-timezone';
import { debounce } from 'underscore';
import { MessageService } from 'primeng/api';
import { Observable, Subscription, filter } from 'rxjs';
import { tap } from 'rxjs/operators';

import { InteractionModel } from '../../../../../Discover/Targeting/models/interaction-model';
import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { DropdownOptionModel } from '../../../../../Common/Forms/models/dropdown-option.model';
import { InteractionType } from '../../../../../api/targeting/enum/interaction-type.enum';
import { TimeModel } from '../../../../../Common/Forms/models/time.model';
import { TimeHelper } from '../../../../../Common/Forms/helpers/time.helper';
import { timeFormatsEnum } from '../../../../../Core/Users/enums/time-formats.enum';
import { MinuteModel } from '../../../../../Common/Forms/models/minute.model';
import { TwentyFourHourModel } from '../../../../../Common/Forms/models/twenty-four-hour.model';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { InteractionViewService } from '../../../services/interaction-view.service';
import { InteractionApiModel } from '../../../../../api/targeting/model/interaction-api.model';
import { EntityType } from '../../../../../api/targeting/enum/entity.type.enum';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { UserModel } from '../../../../../Core/Users/models/user.model';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { FocusHelper } from '../../../../Forms/helpers/focus.helper';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';

interface InteractionForm {
    type: FormControl<DropdownOptionModel<InteractionType>>;
    date: FormControl<Date>;
    time: FormControl<TimeModel>;
    summary: FormControl<string>;
    notes: FormControl<string>;
}

@Component({
    selector: 'agpr-add-interaction-dialog',
    templateUrl: './add-interaction-dialog.component.html',
    styleUrls: ['./add-interaction-dialog.component.scss'],
    standalone: false
})
export class AddInteractionDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() set setInteraction(interaction: InteractionModel) {
        if (interaction) {
            this.interaction = interaction;

            this.formGroup.setValue({
                type: this.typeOptions.find(x => x.value === interaction.type),
                date: this.convertToDate(this.interaction.date),
                time: this.timeHelper.getTimeFromDate(
                    new Date(this.interaction.date),
                    this.interaction.timeZone,
                    this.timeFormat
                ),
                summary: this.interaction.summary,
                notes: this.interaction.notes,
            });
        } else {
            this.formGroup.patchValue({
                time: this.timeHelper.getTimeFromDate(
                    new Date(),
                    this.userTimeZonePreference,
                    this.timeFormat
                ),
            });
        }
    }
    @Input() entityType: EntityType;
    @Input() entityId: string;
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    formGroup: FormGroup<InteractionForm>;
    loading: boolean = false;

    submitTouched: boolean = false;
    debouncedUpdateInteractionDate = debounce(() => this.updateDate(), 150);
    timeFormat: timeFormatsEnum = timeFormatsEnum.twelveHour;

    private interaction: InteractionModel = null;
    private createdUser: UserModel;
    private userTimeZonePreference: string = null;
    private subscriptions: Subscription[] = [];

    readonly id = 'addInteractionDialog';
    readonly interactionTypes = InteractionType;

    readonly buttonTypes = ButtonTypesEnum;
    readonly typeOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Discover.interactionView.type.email', InteractionType.ADHOC_EMAIL),
        new DropdownOptionModel('Discover.interactionView.type.message', InteractionType.ADHOC_MESSAGE),
        new DropdownOptionModel('Discover.interactionView.type.phone', InteractionType.ADHOC_PHONE),
        new DropdownOptionModel('Discover.interactionView.type.inquiry', InteractionType.ADHOC_INBOUND_INQUIRY),
        new DropdownOptionModel('Discover.interactionView.type.other', InteractionType.ADHOC_OTHER),
    ];

    constructor(
        public coreDialogService: CoreDialogService,
        public timeHelper: TimeHelper,
        public currentUserStateService: CurrentUserStateService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public el: ElementRef,
        private interactionViewService: InteractionViewService
    ) {
        super(coreDialogService, 'AddInteraction');

        this.formGroup = new FormGroup<InteractionForm>({
            type: new FormControl(null, Validators.required),
            date: new FormControl(new Date(), Validators.required),
            time: new FormControl(null),
            summary: new FormControl('', Validators.required),
            notes: new FormControl('')
        });
    }

    ngOnInit() {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    this.userTimeZonePreference = preferences.timeZone;
                    this.timeFormat = preferences.timeFormat;

                    if (this.time.value.format !== this.timeFormat) {
                        this.time.value.setFormat(this.timeFormat);

                        this.formGroup.patchValue({
                            time: this.time.value
                        });
                    }

                    if (!this.time.value.timeZone) {
                        this.time.value.setTimeZone(this.userTimeZonePreference);
                        this.formGroup.patchValue({
                            time: this.time.value
                        });
                    }
                }
            ),
            this.currentUserStateService.getUser().subscribe(user => this.createdUser = user)
        );
    }

    get type() {
        return this.formGroup.get('type');
    }

    get date() {
        return this.formGroup.get('date');
    }

    get time() {
        return this.formGroup.get('time');
    }

    get summary() {
        return this.formGroup.get('summary');
    }

    get notes() {
        return this.formGroup.get('notes');
    }


    save() {
        this.submitTouched = true;

        if (this.formGroup.invalid) {
            FocusHelper.focusFirstError(this.el.nativeElement);
            return;
        }

        this.loading = true;
        const newInteraction: InteractionApiModel = {
            id:             this.interaction ? this.interaction.id : undefined,
            type:           this.type.value.value || null,
            date:           this.date.value.getTime(),
            timeZone:       this.time.value.timeZone,
            summary:        this.summary.value,
            notes:          this.notes.value,
            contactUuid:    this.entityType === EntityType.CONTACT ? this.entityId : null,
            outletUuid:     this.entityType === EntityType.OUTLET ? this.entityId : null,
            interactorType: this.entityType,
            createdUserId:  this.interaction ? this.interaction.createdUserId : this.createdUser.id,
        };

        if (this.interaction) {
            this.interactionViewService.editInteraction(newInteraction).pipe(
                tap(_ => this.loading = false)
            ).subscribe(() => {
                this.closeDialog(true);
            });
        } else {
            this.interactionViewService.addInteraction(newInteraction).pipe(
                tap(_ => this.loading = false)
            ).subscribe(() => {
                this.closeDialog(true);
            });
        }
    }

    updateDate() {
        // Moment only accepts 24 hour formatted hours, so the format is converted
        const interactionDate: Date = this.interaction ? new Date(this.interaction.date) : this.date.value;

        const month: number = moment(this.date.value).month();
        const day: number = moment(this.date.value).date();
        const year: number = moment(this.date.value).year();
        const hour: TwentyFourHourModel = this.timeHelper.changeFormat(this.time.value, timeFormatsEnum.twentyFourHour).hour;
        const minute: MinuteModel = this.time.value.minute;

        let newInteractionDate: moment.Moment;

        if (interactionDate && this.date.value instanceof Date) {
            newInteractionDate = moment(interactionDate);
            // Set the new time zone
            newInteractionDate.tz(this.time.value.timeZone);
            // Add the time in the new time zone
            newInteractionDate.year(year);
            newInteractionDate.month(month);
            newInteractionDate.date(day);
            newInteractionDate.hour(hour);
            newInteractionDate.minute(minute);

            this.formGroup.patchValue({
                date: newInteractionDate.toDate()
            });
        }
    }

    /**
     * Convert the date number in milliseconds to a Date Object.
     * @param date - stored time value in milliseconds since midnight, January 1, 1970 UTC
     */
    private convertToDate(date: number): Date {
        const interactionDateMoment: moment.Moment = moment(date);
        const calendarDateMoment: moment.Moment = moment(new Date());
        calendarDateMoment.month(interactionDateMoment.month());
        calendarDateMoment.date(interactionDateMoment.date());
        calendarDateMoment.year(interactionDateMoment.year());
        calendarDateMoment.hour(interactionDateMoment.hour());
        calendarDateMoment.minute(interactionDateMoment.minute());

        return calendarDateMoment.toDate();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
